/** Layout for dashboard type pages with sidebar for navigation */
import { ReactComponent as ChevronIcon } from '../../../assets/icons/chevron-sharp.svg';
import { dateFormat, numberToCurrency } from '../../../utils/translate';
import { Footer, Link, MobileHeader, Sidebar } from '../../../components';
import { getMenuItems } from '../../../utils/getMenuItems';
import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { urlBundlePreloadHashMap } from '../../../Router/Router';
import { useApplication, useAuth, useClient } from '../../../hooks/webapi';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import DiscountBanner from '../../../components/DiscountBanner/DiscountBanner';
import Notifications from '../Notifications/Notifications';
import OverdueBanner from '../../../components/OverdueBanner/OverdueBanner';
import styles from './Dashboard.module.scss';
import Tracked from '../../../customAnalytics/components/Tracked';
import useApplicationProposals from '../../../hooks/webapi/useApplicationProposals';
import useMenuItems from '../../../hooks/useMenuItems';

type Discount = {
  discount: number | undefined;
  dueDate: string | null | undefined;
  minValue: number | undefined;
  maxValue: number | undefined;
};

type Props = {
  backUrlDisabled?: boolean;
  children: ReactNode;
  childrenTop?: ReactNode;
  menuDisabled?: boolean;
  mobileHeading?: string | ReactNode;
  className?: string;
  small?: boolean;
  backUrl?: string;
  dataTest?: string;
  mobileClassName?: string;
  discountObject?: Discount | null;
  isLoadingOffer?: boolean;
};

export default memo(function Dashboard({
  children,
  childrenTop,
  mobileHeading,
  className,
  small = false,
  backUrl = '',
  backUrlDisabled = false,
  dataTest,
  menuDisabled = false,
  mobileClassName,
  discountObject,
  isLoadingOffer,
}: Props) {
  const { t } = useTranslation('common');
  const { client, fetchClient } = useClient();
  const { fetchApplication } = useApplication();
  const { logout } = useAuth();
  const menuItems = useMenuItems({ menuDisabled });
  const { fetchApplicationProposals } = useApplicationProposals();
  const activeDiscount =
    discountObject?.discount && discountObject?.discount > 0;
  const [discountNotification, setDiscountNotification] = useState(true);
  const [pending, setPending] = useState(false);

  function preload() {
    if (urlBundlePreloadHashMap[backUrl]) {
      urlBundlePreloadHashMap[backUrl]();
    }
  }

  useEffect(() => {
    const prefetch = async () => {
      try {
        await fetchClient();
        await fetchApplication();
        await fetchApplicationProposals();
      } catch {}
    };

    prefetch();
  }, [fetchApplication, fetchApplicationProposals, fetchClient]);

  const handleLogout = useCallback(async () => {
    try {
      setPending(true);
      await logout();
    } catch {
      setPending(false);
    }
  }, [logout]);

  const handleDiscountClose = useCallback(() => {
    setDiscountNotification(false);
    sessionStorage.setItem('discountBannerShowed', 'true');
  }, [setDiscountNotification]);

  const discountBannerShowed = sessionStorage.getItem('discountBannerShowed');

  const isCarryOverDiscount = discountObject?.dueDate === null;

  const menuItemsParsed = getMenuItems(menuItems, client);
  return (
    <div className={styles.container} data-test={dataTest}>
      <OverdueBanner />
      {childrenTop}
      {/* TODO: refactor banner to be passed from the child */}
      {!!(discountNotification && activeDiscount && !discountBannerShowed) && (
        <DiscountBanner
          loading={isLoadingOffer}
          discountObject={discountObject}
          handleDiscountClose={handleDiscountClose}
          type={client?.hasOpenLoan ? 'ADDITIONAL_AMOUNT' : 'REPEATED_LOAN'}
          date={
            discountObject?.dueDate
              ? dateFormat(discountObject?.dueDate)
              : undefined
          }
          min={numberToCurrency(Number(discountObject?.minValue))}
          max={numberToCurrency(Number(discountObject?.maxValue))}
          isCarryOverDiscount={isCarryOverDiscount}
        />
      )}
      <div className={styles.app}>
        <MobileHeader
          className={classes(styles['mobile-header'], mobileClassName)}
          menuItems={menuItemsParsed}
          mobileHeading={mobileHeading}
          onLogout={handleLogout}
          backUrl={backUrl}
          backUrlDisabled={backUrlDisabled}
          isSecondaryBackground={!!mobileClassName}
          pending={pending}
        />

        <div className={styles.dashboard}>
          <aside className={styles.aside}>
            <Sidebar
              wrapperClassName={styles.sidebar}
              onLogout={handleLogout}
              menuItems={menuItemsParsed}
              active={window.location.pathname}
              pending={pending}
            />
          </aside>
          <main
            className={classes(styles.main, small && styles.small, className)}
          >
            <Notifications />

            {backUrl && (
              <div className={styles['back-wrapper']}>
                <Tracked description="Back link">
                  <Link
                    className={styles.back}
                    disabled={backUrlDisabled}
                    to={backUrl}
                    onMouseEnter={preload}
                    isButtonPreferred={false}
                  >
                    <ChevronIcon className={styles.chevron} />
                    <span>{t('common:back')}</span>
                  </Link>
                </Tracked>
              </div>
            )}
            {children}
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
});
